/**
 * Оповещения: события и пороги.
 *
 * Правило, из-за которого этот экран выглядит именно так (app-kit.md §4):
 * **важность кодируется словом и положением, цвет появляется только на верхнем
 * уровне.** Она доезжает до клиента ровно такой, какой её присвоило правило, —
 * экран не пересчитывает и не смягчает её.
 *
 * Клиенту видны три ступени: критично, высокая, средняя. Четвёртая ступень
 * спеки §9 — это телеграм-канал команды, а не интерфейс, поэтому «низкая»
 * показывается как средняя, строкой без ребра.
 *
 * **Тревога никогда не появляется без полноты сетки и уверенности рядом**
 * (спека §9): без них «упало на 20 п. п.» может означать, что просто не
 * доехала половина ответов.
 *
 * Аномалия движка помечается технической: это сбой источника, а не падение
 * бренда, и говорить о ней иначе — значит пугать клиента чужой поломкой.
 */

import { useState } from 'react';

import { api, request } from '../api/client';
import { useAccess } from '../session';
import { useScope } from '../scope';
import { ScreenHead } from '../ui/screen';
import { useRead, type Page } from '../ui/query';
import { Segments } from '../ui/controls';
import { Empty, ErrorNotice, Skeleton } from '../ui/state';
import {
  NOT_MEASURED,
  confidenceShare,
  confidenceWord,
  countOf,
  dateNumeric,
  dateProse,
  int,
} from '@shared/src/format';
import { SEVERITY_WORDS } from '@shared/src/product';
import type { components } from '../api/schema';

type Alert = components['schemas']['Alert'];
type AlertRule = components['schemas']['AlertRule'];
type AlertSeverity = components['schemas']['AlertSeverity'];

/**
 * Слово важности — из общего модуля, того же, которым подписан лендинг.
 *
 * Ниже средней у клиента ступени нет — она у команды. Слова приведены к одной
 * грамматике: `Критично` задаёт дизайн-система (app-kit.md §4), а вторая и
 * третья ступени раньше набирались полными прилагательными женского рода
 * («Высокая», «Средняя»), из-за чего в столбце, который клиент читает сверху
 * вниз, соседствовали краткая форма среднего рода и два полных прилагательных.
 * Держать их здесь копией нельзя по той же причине, по которой не держат копию
 * формулы: витрина и кабинет разошлись бы молча.
 */
const SEVERITY: Record<AlertSeverity, string> = {
  critical: SEVERITY_WORDS.critical,
  high: SEVERITY_WORDS.high,
  medium: SEVERITY_WORDS.medium,
  low: SEVERITY_WORDS.low,
};

/** Ребро несут только две верхние ступени; техническое — своё. */
function edgeOf(alert: Alert): string {
  if (alert.is_technical) return ' technical';
  if (alert.severity === 'critical') return ' critical';
  if (alert.severity === 'high') return ' high';
  return '';
}

const SOURCE: Record<AlertRule['source'], string> = {
  default: 'умолчание',
  organization: 'настроено организацией',
  brand: 'настроено для бренда',
};

type Tab = 'events' | 'rules';

const TABS = [
  { value: 'events' as const, label: 'События' },
  { value: 'rules' as const, label: 'Пороги' },
];

export function Alerts({ attention = false }: { attention?: boolean }) {
  const access = useAccess();
  const [tab, setTab] = useState<Tab>('events');

  // Пороги видит не всякая роль: у клиента агентства раздела нет вовсе, и
  // переключателя на него тоже быть не должно.
  const canSeeRules = access.reaches('GET', '/alert-rules');

  return (
    <>
      <ScreenHead
        title={attention ? 'Требует внимания' : 'Оповещения'}
        tools={
          canSeeRules && !attention ? (
            <Segments label="Раздел" options={TABS} value={tab} onChange={setTab} />
          ) : undefined
        }
      />
      {tab === 'rules' && canSeeRules && !attention ? (
        <Rules />
      ) : (
        <Events
          attention={attention}
          // Пустому состоянию положено действие (app-kit.md §5), и здесь оно
          // одно осмысленное: посмотреть, на что вообще настроены пороги.
          onOpenRules={canSeeRules && !attention ? () => setTab('rules') : undefined}
        />
      )}
    </>
  );
}

/* ───────────────────────────── список событий ─────────────────────────────── */

function Events({
  attention,
  onOpenRules,
}: {
  attention: boolean;
  onOpenRules?: () => void;
}) {
  const access = useAccess();
  const scope = useScope();
  const [unreadOnly, setUnreadOnly] = useState(attention);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const list = useRead<Page<Alert>>('/alerts', {
    limit: 50,
    brand_id: scope.brandId ?? undefined,
    unread: unreadOnly ? true : undefined,
  });

  const canRead = access.reaches('PATCH', '/alerts/{alertId}');

  if (list.kind === 'failed') return <ErrorNotice error={list.error} />;
  if (list.kind === 'loading') return <Skeleton rows={3} />;

  const items = attention
    ? list.data.items.filter(
        (alert) => alert.severity === 'critical' || alert.severity === 'high',
      )
    : list.data.items;
  const unread = list.data.items.filter((alert) => !alert.is_read).length;

  const markOne = (alert: Alert) => {
    setBusy(true);
    setFailure(null);
    api
      .patch<Alert>(`/alerts/${alert.id}`, { is_read: true })
      .then((updated) =>
        list.put({
          ...list.data,
          items: list.data.items.map((one) => (one.id === updated.id ? updated : one)),
        }),
      )
      .catch(setFailure)
      .finally(() => setBusy(false));
  };

  const markAll = () => {
    setBusy(true);
    setFailure(null);
    const at = new Date().toISOString();
    api
      .post('/alerts/read', {
        brand_id: scope.brandId ?? undefined,
        before: at,
      })
      .then(() =>
        list.put({
          ...list.data,
          items: list.data.items.map((one) => ({ ...one, is_read: true, read_at: at })),
        }),
      )
      .catch(setFailure)
      .finally(() => setBusy(false));
  };

  return (
    <>
      <div className="filters">
        <Segments
          label="Что показать"
          options={[
            { value: 'unread' as const, label: 'Непрочитанные' },
            { value: 'all' as const, label: 'Все' },
          ]}
          value={unreadOnly ? 'unread' : 'all'}
          onChange={(value) => setUnreadOnly(value === 'unread')}
        />
        {canRead && unread > 0 ? (
          <button
            className="btn btn-ghost btn-sm"
            type="button"
            onClick={markAll}
            disabled={busy}
          >
            Отметить всё прочитанным
          </button>
        ) : null}
      </div>

      {failure ? <ErrorNotice error={failure} /> : null}

      {items.length === 0 ? (
        <Empty
          eyebrow={attention ? 'Всё спокойно' : 'Срабатываний не было'}
          title={
            attention ? 'Ничего не требует внимания' : 'Оповещений за этот период не было'
          }
          action={
            onOpenRules ? (
              <button className="btn btn-ghost" type="button" onClick={onOpenRules}>
                Открыть пороги
              </button>
            ) : undefined
          }
        >
          {attention
            ? 'Критичных и важных оповещений нет. Умеренные по-прежнему видны в разделе «Оповещения» — они не требуют реакции в тот же день.'
            : 'Пороги настроены, замеры идут, срабатываний нет. Это нормальное состояние, а не пустой экран.'}
        </Empty>
      ) : (
        <div className="items">
          {items.map((alert) => (
            <article
              key={alert.id}
              className={`item${edgeOf(alert)}${alert.is_read ? '' : ' unread'}`}
            >
              <div className="item-mark">
                <b>{SEVERITY[alert.severity]}</b>
                {alert.is_technical ? 'Техническое' : null}
                <span className="stamp">{dateNumeric(alert.occurred_at)}</span>
              </div>

              <div className="item-body">
                <h3>{alert.title}</h3>
                {alert.body ? <p>{alert.body}</p> : null}

                {/* Пояснения «это сбой источника» здесь больше нет, и это не
                    сокращение текста. Оно печаталось БЕЗУСЛОВНО при is_technical
                    и утверждало, что «движок отвечал ошибками или не ответил
                    вовсе», — а правило «аномалия движка» по построению
                    срабатывает только на движке, который ОТВЕТИЛ: в отбор идут
                    движки с organic_answers > 0. При шести органических ответах
                    из шести клиент читал, что источник сломался, и шёл искать
                    аварию вместо изменения выдачи.

                    Причину теперь называет само правило, в `body`: оно одно
                    знает, что именно сработало. Общий текст на все технические
                    тревоги сразу не может быть верным для каждой. */}

                {/* Тревога без основания не показывается: полнота сетки и
                    уверенность стоят рядом всегда (спека §9). */}
                <span className="stamp">
                  {alert.brand_name ? `${alert.brand_name} · ` : ''}
                  {alert.snapshot_date ? `${dateProse(alert.snapshot_date)} · ` : ''}
                  {alert.grid_complete === false
                    ? 'сетка неполная'
                    : 'сетка закрыта полностью'}{' '}
                  ·{' '}
                  {alert.confidence === null || alert.confidence === undefined
                    ? `уверенность ${NOT_MEASURED.toLowerCase()}`
                    : `уверенность ${confidenceWord(alert.confidence)} (${confidenceShare(alert.confidence)} согласия прогонов)`}
                </span>
              </div>

              {canRead && !alert.is_read ? (
                <div className="item-act">
                  <button
                    className="btn btn-ghost btn-sm"
                    type="button"
                    disabled={busy}
                    onClick={() => markOne(alert)}
                  >
                    {/* Контрол называется по результату, а не по состоянию
                        (voice.md): «Прочитано» — это то, что будет написано
                        рядом после нажатия, а не то, что делает кнопка. */}
                    Отметить прочитанным
                  </button>
                </div>
              ) : (
                <div className="item-act">
                  <span className="stamp">
                    {alert.read_at ? `Прочитано ${dateNumeric(alert.read_at)}` : ''}
                  </span>
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </>
  );
}

/* ──────────────────────────────── пороги ──────────────────────────────────── */

function Rules() {
  const access = useAccess();
  const scope = useScope();
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);

  const list = useRead<AlertRule[]>('/alert-rules', {
    brand_id: scope.brandId ?? undefined,
  });

  const canEdit = access.reaches('PUT', '/alert-rules/{ruleCode}');

  if (list.kind === 'failed') return <ErrorNotice error={list.error} />;
  if (list.kind === 'loading') return <Skeleton rows={3} />;

  const save = (rule: AlertRule, patch: Partial<AlertRule>) => {
    setBusy(rule.rule_code);
    setFailure(null);
    request<AlertRule>(`/alert-rules/${rule.rule_code}`, {
      method: 'PUT',
      body: {
        brand_id: scope.brandId ?? null,
        is_enabled: patch.is_enabled ?? rule.is_enabled,
        threshold: patch.threshold ?? rule.threshold ?? null,
        critical_threshold: patch.critical_threshold ?? rule.critical_threshold ?? null,
      },
    })
      .then((updated) =>
        list.put(
          list.data.map((one) => (one.rule_code === updated.rule_code ? updated : one)),
        ),
      )
      .catch(setFailure)
      .finally(() => setBusy(null));
  };

  const reset = (rule: AlertRule) => {
    setBusy(rule.rule_code);
    setFailure(null);
    api
      .delete(`/alert-rules/${rule.rule_code}`, {
        query: { brand_id: scope.brandId ?? undefined },
      })
      .then(() => list.reload())
      .catch(setFailure)
      .finally(() => setBusy(null));
  };

  return (
    <>
      {failure ? <ErrorNotice error={failure} /> : null}

      <div className="tbl-wrap block">
        <table className="mini">
          <caption>
            Пороги оповещений{scope.brand ? ` для бренда «${scope.brand.name}»` : ''}
          </caption>
          <thead>
            <tr>
              <th scope="col">Правило</th>
              <th
                scope="col"
                className="num"
                title="Порог в процентных пунктах или позициях"
              >
                Порог
              </th>
              <th
                scope="col"
                className="num"
                title="Вторая ступень того же правила: с этого значения важность становится критичной"
              >
                2-я ступень
              </th>
              <th scope="col">Важность</th>
              <th scope="col">Условия</th>
              <th scope="col">Откуда значение</th>
              {canEdit ? <th scope="col">&nbsp;</th> : null}
            </tr>
          </thead>
          <tbody>
            {list.data.map((rule) => (
              <tr key={rule.rule_code}>
                <td>
                  <b>{rule.title}</b>
                  {/* Выключенное правило говорит об этом словом. Догадываться о
                      состоянии по подписи на кнопке человек не обязан. */}
                  {rule.is_enabled ? null : (
                    <span className="stamp block">
                      Выключено — оповещения не поднимаются
                    </span>
                  )}
                  {rule.description ? (
                    <span className="stamp block">{rule.description}</span>
                  ) : null}
                </td>
                <td className="tnum num">
                  {rule.threshold === null || rule.threshold === undefined
                    ? '—'
                    : int(rule.threshold)}
                </td>
                <td className="tnum num">
                  {rule.critical_threshold === null ||
                  rule.critical_threshold === undefined
                    ? '—'
                    : int(rule.critical_threshold)}
                </td>
                <td>
                  {SEVERITY[rule.severity]}
                  {rule.is_technical ? ' · техническое' : ''}
                </td>
                <td className="stamp">
                  {rule.require_complete_grid === false
                    ? 'считается и на неполной сетке'
                    : 'только на полной сетке'}
                  {rule.min_confidence !== null && rule.min_confidence !== undefined
                    ? ` · уверенность от ${confidenceShare(rule.min_confidence)}`
                    : ''}
                  {rule.dedup_hours
                    ? ` · дубли не чаще ${countOf(rule.dedup_hours, 'часа', 'часов', 'часов')}`
                    : ''}
                </td>
                <td className="stamp">{SOURCE[rule.source]}</td>
                {canEdit ? (
                  <td>
                    <div className="item-act">
                      <button
                        className="btn btn-ghost btn-sm"
                        type="button"
                        disabled={busy === rule.rule_code}
                        onClick={() => save(rule, { is_enabled: !rule.is_enabled })}
                      >
                        {rule.is_enabled ? 'Выключить' : 'Включить'}
                      </button>
                      {rule.source !== 'default' ? (
                        <button
                          className="btn btn-ghost btn-sm"
                          type="button"
                          disabled={busy === rule.rule_code}
                          onClick={() => reset(rule)}
                        >
                          К умолчанию
                        </button>
                      ) : null}
                    </div>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="stamp basis">
        Выключенное правило перестаёт слать письма и поднимать оповещения немедленно. Уже
        поднятые остаются в списке событий.
      </p>
    </>
  );
}
